import { analyticsHelpers } from 'day8-ui';
import { ACTIONS, PLAN } from '../constants';
import { maxBoatCapacityByDestination } from '../helpers/crew';
import { yachtCardFromStandardYachtWeek } from '../helpers/yachts';
import { isWeekComingSoon, isWeekInvisible, isWeekSoldOut } from '../helpers/week';
import { getSelectedFilters } from '../helpers/filter';

export const fetchBooking = planCode => dispatch =>
  fetch(`/api/plan/fetch/${planCode}`, {
    method: 'POST',
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        const booking = response.data.booking;
        if (response.data && response.data.booking && response.data.booking.boatWeek) {
          booking.boatWeek = response.data.boatWeekJson;
        }
        return dispatch({
          type: ACTIONS.UPDATE_PLAN_BOOKING,
          booking,
          comments: response.data.comments,
          bestWeeks: response.data.bestWeeks,
          available: response.data.available,
          sameYachtPlanNum: response.data.sameYachtPlanNum
        });
      }
      return response;
    })
    .catch(ex => console.error('failed fetching booking', ex));

export const addComment = (planCode, text) => dispatch =>
  fetch(`/api/plan/${planCode}/add-comment`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      text
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        return dispatch({
          type: ACTIONS.ADD_COMMENT,
          comment: response.data.comment
        });
      }
      return response;
    });

export const fetchWeeks = () => dispatch =>
  fetch('/api/plan/upcoming-weeks')
    .then(response => response.json())
    .then(response => {
      if (response.status === 'success') {
        dispatch({ type: ACTIONS.UPDATE_WEEKS, weeks: response.data });
      }
      return response;
    });

const deNormalizeData = data => {
  const boats = data.boats;
  const weeks = data.weeks;
  return data.boatWeeks.map(bw => ({
    ...bw,
    boat: {
      ...boats[bw.boat],
      capacity: maxBoatCapacityByDestination(boats[bw.boat].capacity, weeks[bw.week])
    },
    week: weeks[bw.week]
  }));
};

export const fetchYachts = destinationId => dispatch =>
  fetch('/api/yl/slim', {
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(response => {
      let boatWeeks = deNormalizeData(response.data);
      if (destinationId) {
        boatWeeks = boatWeeks.filter(bw => +bw.week.destinationId === +destinationId);
      }
      dispatch({
        type: ACTIONS.UPDATE_YACHTS,
        yachts: boatWeeks.filter(bw =>
          !isWeekInvisible(bw.week) &&
          !isWeekSoldOut(bw.week) &&
          !isWeekComingSoon(bw.week)
        )
      });
    })
    .catch(ex => console.log('failed fetching yacht weeks...', ex));

// Filter planner yachts based on filter criteria (simplified version without destination/crew filtering)
export const filterPlannerYachts = location => dispatch => {
  const {
    hullType,
    minCapacity,
    maxCapacity,
    marina,
    amenities,
    weekFilter,
    boatYear,
    boatCabins,
    boatSize,
    minPrice,
    maxPrice,
    countryFilter,
    festivalFilter
  } = getSelectedFilters(location);

  const priceRange = {
    min: minPrice,
    max: maxPrice
  };

  dispatch({
    type: 'FILTER_PLANNER_YACHTS',
    payload: {
      hullType,
      amenities,
      marina,
      capacity: { minCapacity, maxCapacity },
      boatYear,
      boatCabins,
      boatSize,
      priceRange,
      weekFilter,
      countryFilter,
      festivalFilter
    }
  });
};

export const voteOnWeek = (week, action, booking) => dispatch => {
  return fetch(`/api/plan/possible-weeks/${booking.id}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      week,
      action
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        return dispatch({
          type: ACTIONS.UPDATE_WEEK_VOTE,
          crewMember: response.data.crewMember,
          bestWeeks: response.data.bestWeeks
        });
      }
      return response;
    });
};

export const sendCrewInvitation = (booking, emails) => () => {
  const alphaId = booking.alphaId;
  return fetch(`/api/plan/${alphaId}/invite`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      emails
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === 'success') {
        // @TODO: add action and dispatch
        // dispatch({
        //   type: 'PLAN_INVITE_EMAILS',
        //   crewInvites: response.data.crewInvites,
        // });
      }
      return response;
    });
};

export const changeCrewRatio = (planCode, numBoys, numGirls) => dispatch => {
  return fetch(`/api/plan/${planCode}/crew-ratio`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      numBoys,
      numGirls
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        return dispatch({
          type: ACTIONS.UPDATE_CREW_RATIO,
          numBoys,
          numGirls
        });
      }
      if (response.status === 'failed') {
        throw new Error(response.message);
      }
      return response;
    });
};

export const setPersonnel = (planCode, skipper, hostess) => dispatch => {
  fetch(`/api/plan/${planCode}/personnel`, {
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      hostess,
      skipper
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        return dispatch({
          type: ACTIONS.UPDATE_PERSONNEL,
          skipper,
          host: hostess
        });
      }
      return response;
    });
};

export const updatePricing = () => (dispatch, getState) => {
  // Use with caution, created for the case where hostess is updated elsewhere in plan
  const { planner } = getState();
  const pricing = {
    ...planner.pricing,
    [PLAN.PRICING.HOST]: {
      ...planner.pricing[PLAN.PRICING.HOST],
      selected: !!planner.plan.needHostess
    }
  };
  dispatch({
    type: ACTIONS.UPDATE_PRICING,
    pricing
  });
  return pricing;
};

export const updateCrewMember = (crewMember, status) => dispatch =>
  fetch(`/api/plan/status/${crewMember.id}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({ status })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === 'success') {
        dispatch({
          type: ACTIONS.UPDATE_CREW_MEMBER,
          crewMember,
          status
        });
      }
      return response;
    });

export const cancelPlan = (planCode, reason) => dispatch =>
  fetch(`/api/plan/${planCode}/cancel`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      reason
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.CANCEL_PLAN,
          activePlans: response.data.activePlans,
          planCode
        });
      }
      return response;
    });


export const updateLeadBooker = (booking, member) => dispatch =>
  fetch(`/api/plan/change-booker/${booking.id}/${member.id}`, {
    method: 'POST',
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_LEAD_BOOKER,
          booker: member
        });
      }
      return response;
    });

export const fetchDestinations = () => dispatch =>
  fetch('/api/yl/destinations', {
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(response => dispatch({
      type: 'FETCH_DESTINATIONS',
      destinations: response.map(destination => {
        const weeksWithDestination = destination.weeks.filter(w => !isWeekInvisible(w) && !isWeekComingSoon(w) && !isWeekSoldOut(w)).map(week => {
          // `weeks` is only pulled out so the rest is the destination without them
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { weeks, ...destinationWithoutWeeks } = destination;
          return { ...week, destination: destinationWithoutWeeks };
        });
        return {
          ...destination,
          weeks: weeksWithDestination
        };
      }).filter(destination => destination.weeks.length > 0)
    })).catch(ex => console.log('failed fetching destinations...', ex));

export const changeDestination = (planCode, destinationId) => dispatch =>
  fetch(`/api/plan/${planCode}/change-destination`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      destinationId
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        return dispatch({
          type: ACTIONS.UPDATE_DESTINATION,
          booking: response.data.plan,
          pricing: response.data.pricing
        });
      }
      return response;
    });

export const updateTitle = (bookingId, title) => dispatch =>
  fetch(`/api/plan/title/${bookingId}`, {
    method: 'POST',
    credentials: 'same-origin',
    body: JSON.stringify({
      title
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_TITLE,
          title
        });
      }
      return response;
    });

export const bookPlan = planCode => dispatch =>
  fetch(`/api/plan/${planCode}/book`, {
    method: 'GET',
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.BOOK_PLAN
        });
      }
      return response;
    });

export const addYachtToPlan = (planCode, boatWeekId, standardYachtWeekId) => dispatch =>
  fetch(`/api/plan/${planCode}/add-yacht`, {
    credentials: 'same-origin',
    method: 'POST',
    body: JSON.stringify({
      boatWeekId,
      standardYachtWeekId
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        const booking = response.data.plan;
        if (response.data.boatWeekJson) {
          booking.boatWeek = response.data.boatWeekJson;
        }
        dispatch({
          type: ACTIONS.UPDATE_PLAN,
          booking: response.data.plan,
          standardYacht: response.data.standardYachtWeek && yachtCardFromStandardYachtWeek(response.data.standardYachtWeek, booking.week)
        });
      }
      return response;
    });

export const removeYachtFromPlan = planCode => dispatch =>
  fetch(`/api/plan/${planCode}/remove-yacht`, {
    credentials: 'same-origin',
    method: 'POST'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_PLAN,
          booking: response.data
        });
      }
      return response;
    });

export const createNormalYachtPlan = (destinationId, boatWeekId, hostess, girls, boys) => dispatch =>
  fetch('/api/plan/start', {
    credentials: 'same-origin',
    method: 'POST',
    body: JSON.stringify({
      destinationId,
      boatWeekId,
      hostess,
      girls,
      boys
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        analyticsHelpers.createPlan(response.data.booking, response.data.recentSavedPlans, response.data.recentJoinedPlans);
        dispatch({
          type: ACTIONS.CREATE_PLAN,
          booking: response.data.booking,
          standardYacht: response.data.standardYacht
        });
      }
      return response;
    });

export const createStandardYachtPlan = (standardYachtWeekId, hostess, girls, boys) => dispatch =>
  fetch('/api/plan/standard-start', {
    credentials: 'same-origin',
    method: 'POST',
    body: JSON.stringify({
      standardYachtWeekId,
      hostess,
      girls,
      boys
    })
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        analyticsHelpers.createPlan(response.data.booking, response.data.recentSavedPlans, response.data.recentJoinedPlans);
        dispatch({
          type: ACTIONS.CREATE_PLAN,
          booking: response.data.booking,
          standardYacht: response.data.standardYacht
        });
      }
      return response;
    });
// All plans including booker and invited
export const fetchActivePlans = () => dispatch =>
  fetch('/api/plan/landing', {
    credentials: 'same-origin',
    method: 'GET'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_ACTIVE_PLANS,
          activePlans: response.data.activePlans
        });
      }
      return response;
    });

export const fetchPricing = planCode => dispatch =>
  fetch(`/api/plan/pricing/${planCode}`, {
    credentials: 'same-origin',
    method: 'GET'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_PRICING,
          pricing: response.data
        });
      }
      return response.data;
    });

export const fetchIsCrew = planCode => dispatch =>
  fetch(`/api/plan/${planCode}/is-crew`, {
    credentials: 'same-origin',
    method: 'GET'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_IS_CREW,
          isCrew: response.data.isCrew
        });
      }
      return response;
    });

export const fetchJoinData = planCode => dispatch =>
  fetch(`/api/plan/${planCode}/join-data`, {
    credentials: 'same-origin',
    method: 'GET'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        dispatch({
          type: ACTIONS.UPDATE_JOIN_DATA,
          data: response.data
        });
      }
    });

export const joinPlan = planCode => dispatch =>
  fetch(`/api/plan/${planCode}/join`, {
    credentials: 'same-origin',
    method: 'GET'
  })
    .then(response => response.json())
    .then(response => {
      if (response.status === ACTIONS.SUCCESS) {
        analyticsHelpers.joinedPlan(response.data.bookingId);
        dispatch({
          type: ACTIONS.UPDATE_IS_CREW,
          isCrew: true
        });
      }
    });
